import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePageLoaderContext } from '../../contexts/PageLoaderContext';
import { useNotification } from '../../contexts/NotificationContext';
import { UserPermission } from '../../types';
import apiService from '../../services/api';
import styles from '../../styles/AdminDashboard.module.css';
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  DoughnutController,
  LineController,
  BarController,
  Filler
} from 'chart.js';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, DoughnutController, LineController, BarController, Filler);

interface DashboardStats {
  total_users: number;
  total_groups: number;
  task_categories: number;
  weekly_completed_tasks: number;
  monthly_points: number;
  pending_approvals: number;
  total_users_trend: number;
  trends?: {
    users?: number;
    tasks?: number;
    points?: number;
  };
}

interface Activity {
  id: string;
  title: string;
  user: string;
  group: string;
  time: string;
  type: 'success' | 'warning' | 'info';
  points?: number;
}

interface TopPerformer {
  id: string;
  name: string;
  tasks: number;
  progress: number;
  color: string;
}

interface ChartData {
  weekly_tasks?: number[];
  daily_tasks?: {
    completed: number[];
    pending: number[];
  };
  task_distribution?: {
    completed: number;
    in_progress: number;
    pending: number;
  };
  completion_percentage?: number;
}

interface NavItem {
  path: string;
  label: string;
  icon: string;
  module: string | null;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const notificationService = useNotification();
  const lineChartRef = useRef<HTMLCanvasElement>(null);
  const barChartRef = useRef<HTMLCanvasElement>(null);
  const donutChartRef = useRef<HTMLCanvasElement>(null);
  const lineChartInstance = useRef<Chart | null>(null);
  const barChartInstance = useRef<Chart | null>(null);
  const donutChartInstance = useRef<Chart | null>(null);

  const [stats, setStats] = useState<DashboardStats>({
    total_users: 0,
    total_groups: 0,
    task_categories: 0,
    weekly_completed_tasks: 0,
    monthly_points: 0,
    pending_approvals: 0,
    total_users_trend: 0,
  });

  const [activities, setActivities] = useState<Activity[]>([]);
  const [topPerformers, setTopPerformers] = useState<TopPerformer[]>([]);
  const [chartData, setChartData] = useState<ChartData>({});
  const [loading, setLoading] = useState(true);
  const [platformNavItems, setPlatformNavItems] = useState<NavItem[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [isMainAdmin, setIsMainAdmin] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [userRole, setUserRole] = useState<'admin' | 'group_admin' | 'member'>('member');

  // Get icons from Sidebar (same icon system)
  const getIcon = (iconName: string) => {
    const icons: { [key: string]: React.ReactElement } = {
      home: (
        <>
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <path d="M9 22V12h6v10" />
          <path d="M12 12h3" />
        </>
      ),
      users: (
        <>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </>
      ),
      groups: (
        <>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </>
      ),
      points: (
        <>
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </>
      ),
      campus: (
        <>
          <circle cx="12" cy="5" r="2" />
          <circle cx="7" cy="12" r="2" />
          <circle cx="17" cy="12" r="2" />
          <circle cx="5" cy="19" r="2" />
          <circle cx="12" cy="19" r="2" />
          <circle cx="19" cy="19" r="2" />
          <line x1="12" y1="7" x2="7" y2="10" />
          <line x1="12" y1="7" x2="17" y2="10" />
          <line x1="7" y1="14" x2="5" y2="17" />
          <line x1="7" y1="14" x2="12" y2="17" />
          <line x1="17" y1="14" x2="12" y2="17" />
          <line x1="17" y1="14" x2="19" y2="17" />
        </>
      ),
      chart: (
        <>
          <path d="M3 3v18h18" />
          <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
        </>
      ),
      badge: (
        <>
          <circle cx="12" cy="8" r="7"></circle>
          <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
        </>
      ),
      report: (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <line x1="10" y1="9" x2="8" y2="9" />
        </>
      ),
      settings: (
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </>
      ),
      tasks: (
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </>
      ),
      notifications: (
        <>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </>
      ),
      profile: (
        <>
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </>
      ),
      accessibility: (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-universal-access" viewBox="0 0 16 16">
          <path d="M9.5 1.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0M6 5.5l-4.535-.442A.531.531 0 0 1 1.531 4H14.47a.531.531 0 0 1 .066 1.058L10 5.5V9l.452 6.42a.535.535 0 0 1-1.053.174L8.243 9.97c-.064-.252-.422-.252-.486 0l-1.156 5.624a.535.535 0 0 1-1.053-.174L6 9z" />
        </svg>
      ),
    };
    return icons[iconName] || icons.home;
  };

  // Check module permissions (same logic as Sidebar)
  const hasModulePermission = (module: string | null): boolean => {
    if (permissionsLoading) {
      return false;
    }

    if (!module) {
      return true; // Items without module are always visible
    }

    const validActions = ['view', 'add', 'edit', 'delete', 'create', 'update'];
    return userPermissions.some(
      perm => perm.module === module && validActions.includes(perm.action)
    );
  };

  // Should show item (same logic as Sidebar)
  const shouldShowItem = (item: NavItem): boolean => {
    if (item.path === '/permission-management') {
      return isMainAdmin;
    }

    if (item.path === '/platform-reports') {
      return user?.role === 'admin';
    }

    if (!item.module) {
      return true; // Always visible items
    }

    return hasModulePermission(item.module);
  };

  // Get platform management items (same as Sidebar, using same permission logic)
  const getPlatformNavItems = (): NavItem[] => {
    const allItems: NavItem[] = [
      { path: '/group-management/groups', label: 'Group Management', icon: 'groups', module: 'group_management' },
      { path: '/user-management', label: 'User Management', icon: 'users', module: 'user_management' },
      { path: '/campus-administration', label: 'Campus Hierarchy', icon: 'campus', module: 'campus_hierarchy' },
      { path: '/point-scheme', label: 'Point Scheme', icon: 'points', module: 'point_scheme' },
      { path: '/task-management', label: 'Task Management', icon: 'tasks', module: 'task_management' },
      { path: '/leader-board', label: 'Leader Board', icon: 'chart', module: 'leader_board' },
      { path: '/badges', label: 'Badges & Achievements', icon: 'badge', module: 'badge_achievements' },
      { path: '/platform-reports', label: 'Platform Reports', icon: 'report', module: null },
      { path: '/notifications', label: 'Notifications', icon: 'notifications', module: 'notifications' },
      { path: '/my-profile', label: 'Profile', icon: 'profile', module: null },
      { path: '/permission-management', label: 'Accessibility', icon: 'accessibility', module: null },
      { path: '/system-settings', label: 'System Settings', icon: 'settings', module: null },
    ];

    // Filter based on permissions (same logic as Sidebar)
    return allItems.filter(item => shouldShowItem(item));
  };

  // Determine if user is admin based on role
  const isAdmin = user?.role === 'admin';
  const isGroupAdmin = user?.role === 'teamlead' || (user as any)?.is_group_admin;

  // Get group icon based on rank
  const getGroupIcon = (index: number) => {
    // Use users icon for all groups (like the image description)
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  };

  // Load user permissions (same as Sidebar)
  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!user) {
        setPermissionsLoading(false);
        return;
      }

      try {
        setPermissionsLoading(true);
        const response = await apiService.getUserPermissions() as any;
        if (response && response.status === 'success') {
          const responseData = response.data || response;
          const permissions = (responseData.permissions || []) as UserPermission[];
          const isMainAdmin = Boolean(responseData.is_main_admin);

          setUserPermissions(permissions);
          setIsMainAdmin(isMainAdmin);
        } else {
          setUserPermissions([]);
          setIsMainAdmin(false);
        }
      } catch (error) {
        console.error('Error loading user permissions:', error);
        setUserPermissions([]);
        setIsMainAdmin(false);
      } finally {
        setPermissionsLoading(false);
      }
    };

    loadUserPermissions();
  }, [user]);

  // Update platform nav items when permissions change
  useEffect(() => {
    if (!permissionsLoading) {
      const items = getPlatformNavItems();
      setPlatformNavItems(items);
    }
  }, [user, userPermissions, isMainAdmin, permissionsLoading]);

  // Load dashboard data from backend
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        showLoader('Loading dashboard...');

        const response = await apiService.getDashboardStats();

        if (response.status === 'success' && response.data) {
          const data = response.data;

          // Set stats
          if (data.stats) {
            setStats(data.stats);
          }

          // Set chart data
          if (data.chart_data) {
            setChartData(data.chart_data);
          }

          // Set activities
          if (data.recent_activities) {
            setActivities(data.recent_activities);
          }

          // Set top performers
          if (data.top_groups) {
            setTopPerformers(data.top_groups);
          }

          // Set user role
          if (data.user_role) {
            setUserRole(data.user_role);
          }
        } else {
          notificationService.error(response.message || 'Failed to load dashboard data');
        }
      } catch (error: any) {
        console.error('Error loading dashboard data:', error);
        notificationService.error('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
        hideLoader();
      }
    };

    loadDashboardData();
  }, [user]);

  // Initialize charts with real data
  useEffect(() => {
    if (!loading && chartData) {
      setTimeout(() => {
        initializeCharts();
      }, 100);
    }

    return () => {
      // Cleanup charts on unmount
      if (lineChartInstance.current) lineChartInstance.current.destroy();
      if (barChartInstance.current) barChartInstance.current.destroy();
      if (donutChartInstance.current) donutChartInstance.current.destroy();
    };
  }, [loading, chartData]);

  const initializeCharts = () => {
    // Destroy existing charts
    if (lineChartInstance.current) lineChartInstance.current.destroy();
    if (barChartInstance.current) barChartInstance.current.destroy();
    if (donutChartInstance.current) donutChartInstance.current.destroy();

    // Line Chart - Weekly Tasks (last 12 days)
    if (lineChartRef.current && chartData.weekly_tasks) {
      const ctx = lineChartRef.current.getContext('2d')!;
      const gradient = ctx.createLinearGradient(0, 0, 0, 280);
      gradient.addColorStop(0, 'rgba(16, 185, 129, 0.2)');
      gradient.addColorStop(1, 'rgba(16, 185, 129, 0.02)');

      // Calculate last week data for comparison
      const currentWeek = chartData.weekly_tasks.slice(-7);
      const lastWeek = chartData.weekly_tasks.slice(-14, -7);

      // Generate labels for last 12 days
      const labels = [];
      for (let i = 11; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        labels.push(date.getDate().toString().padStart(2, '0'));
      }

      lineChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            {
              label: 'This Week',
              data: chartData.weekly_tasks,
              borderColor: '#10b981',
              backgroundColor: gradient,
              borderWidth: 2.5,
              pointRadius: 0,
              pointHoverRadius: 6,
              pointBackgroundColor: '#10b981',
              pointBorderColor: '#fff',
              pointBorderWidth: 2,
              fill: true,
              tension: 0.4,
            },
            {
              label: 'Last Week',
              data: lastWeek.length > 0 ? lastWeek : currentWeek.map(() => 0),
              borderColor: '#e5e7eb',
              backgroundColor: 'transparent',
              borderWidth: 2,
              pointRadius: 0,
              fill: false,
              tension: 0.4,
            }
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: {
              grid: { display: false, drawBorder: false } as any,
              ticks: { color: '#6b7280', font: { size: 11 } }
            },
            y: {
              grid: { color: 'rgba(0,0,0,0.04)', drawBorder: false } as any,
              beginAtZero: true,
              ticks: { color: '#6b7280', font: { size: 11 } }
            }
          },
          interaction: { intersect: false, mode: 'index' }
        } as any,
      });
    }

    // Bar Chart - Daily Performance (last 7 days)
    if (barChartRef.current && chartData.daily_tasks) {
      const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      barChartInstance.current = new Chart(barChartRef.current, {
        type: 'bar',
        data: {
          labels: dayLabels,
          datasets: [
            {
              label: 'Completed',
              data: chartData.daily_tasks.completed || [],
              backgroundColor: '#1a237e',
              borderRadius: 4,
              barPercentage: 0.6,
            },
            {
              label: 'Pending',
              data: chartData.daily_tasks.pending || [],
              backgroundColor: '#e5e7eb',
              borderRadius: 4,
              barPercentage: 0.6,
            }
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: {
              grid: { display: false } as any,
              ticks: { color: '#6b7280', font: { size: 11 } }
            },
            y: {
              grid: { color: 'rgba(0,0,0,0.04)' } as any,
              beginAtZero: true,
              ticks: { color: '#6b7280', font: { size: 11 } }
            }
          }
        } as any,
      });
    }

    // Donut Chart - Task Distribution
    if (donutChartRef.current && chartData.task_distribution) {
      const dist = chartData.task_distribution;
      const total = dist.completed + dist.in_progress + dist.pending;
      donutChartInstance.current = new Chart(donutChartRef.current, {
        type: 'doughnut',
        data: {
          labels: ['Completed', 'In Progress', 'Pending'],
          datasets: [{
            data: [dist.completed, dist.in_progress, dist.pending],
            backgroundColor: ['#10b981', '#1a237e', '#e8eaf6'],
            borderWidth: 0,
            hoverOffset: 4,
          }],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false }
          },
          cutout: '75%',
        } as any,
      });
    }
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTrend = (value?: number) => {
    if (value === undefined || value === null) return '';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const getTrendClass = (value?: number) => {
    if (value === undefined || value === null) return '';
    return value >= 0 ? styles.up : styles.down;
  };

  if (loading) return null;

  const completionPercentage = chartData.completion_percentage || 0;
  const dist = chartData.task_distribution || { completed: 0, in_progress: 0, pending: 0 };
  const totalTasks = dist.completed + dist.in_progress + dist.pending;
  const completedPercent = totalTasks > 0 ? Math.round((dist.completed / totalTasks) * 100) : 0;
  const inProgressPercent = totalTasks > 0 ? Math.round((dist.in_progress / totalTasks) * 100) : 0;
  const pendingPercent = totalTasks > 0 ? Math.round((dist.pending / totalTasks) * 100) : 0;

  return (
    <div className={styles.pageContainer}>
      {/* Header */}
      <div className={styles.dashboardHeader}>
        <div className={styles.headerLeft}>
          <h1>Dashboard</h1>
          <p>{formatDate()}</p>
        </div>
        <div className={styles.headerRight}>
          <Link to="/platform-reports" className={styles.headerBtn}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></svg>
            Platform Reports
          </Link>
          <Link to="/system-settings" className={styles.headerBtnPrimary}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>
            System Settings
          </Link>
        </div>
      </div>

      {/* Stats Row */}
      <div className={styles.statsRow}>
        {(userRole === 'admin' || userRole === 'group_admin') && (
          <div className={`${styles.statCard} ${styles.blue}`}>
            <div className={`${styles.statIconBox} ${styles.blue}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
            </div>
            <div className={styles.statValue}>{stats.total_users || 0}</div>
            <div className={styles.statLabel}>{isGroupAdmin ? 'Group Members' : 'Total Users'}</div>
            <div className={`${styles.statTrend} ${getTrendClass(stats.total_users_trend)}`}>
              {formatTrend(stats.total_users_trend)} from last week
            </div>
          </div>
        )}


        <div className={`${styles.statCard} ${styles.yellow}`}>
          <div className={`${styles.statIconBox} ${styles.yellow}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
          </div>
          <div className={styles.statValue}>{stats.weekly_completed_tasks}</div>
          <div className={styles.statLabel}>Tasks Completed</div>
          <div className={`${styles.statTrend} ${getTrendClass(stats.trends?.tasks)}`}>
            {formatTrend(stats.trends?.tasks)} from last week
          </div>
        </div>

        <div className={`${styles.statCard} ${styles.green}`}>
          <div className={`${styles.statIconBox} ${styles.green}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>
          </div>
          <div className={styles.statValue}>{stats.monthly_points.toLocaleString()}</div>
          <div className={styles.statLabel}>Total Points</div>
          <div className={`${styles.statTrend} ${getTrendClass(stats.trends?.points)}`}>
            {formatTrend(stats.trends?.points)} from last month
          </div>
        </div>

        <div className={`${styles.statCard} ${styles.purple}`}>
          <div className={`${styles.statIconBox} ${styles.purple}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
          </div>
          <div className={styles.statValue}>{stats.pending_approvals}</div>
          <div className={styles.statLabel}>Pending Approvals</div>
          <div className={`${styles.statTrend} ${styles.neutral}`}>Requires attention</div>
        </div>
      </div>

      {/* Platform Management */}
      <div className={styles.managementSection}>
        <h2 className={styles.sectionTitle}>Platform Management</h2>
        <div className={styles.moduleGrid}>
          {platformNavItems.map((item) => (
            <Link key={item.path} to={item.path} className={styles.moduleLink}>
              {item.icon === 'accessibility' ? (
                getIcon(item.icon)
              ) : (
                <svg className={styles.moduleIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {getIcon(item.icon)}
                </svg>
              )}
              {item.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Main Grid */}
      <div className={styles.mainGrid}>
        {/* Performance Chart */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div>
              <h3 className={styles.cardTitle}>Performance Overview</h3>
              <p className={styles.cardSubtitle}>Tasks completed over time</p>
            </div>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.chartContainer}>
              <canvas ref={lineChartRef}></canvas>
            </div>
            <div className={styles.chartLegend}>
              <div className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: '#10b981' }}></span>
                This Week
              </div>
              <div className={styles.legendItem}>
                <span className={`${styles.legendDot} ${styles.secondary}`}></span>
                Last Week
              </div>
            </div>
          </div>
        </div>

        {/* Task Distribution */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div>
              <h3 className={styles.cardTitle}>Task Status</h3>
              <p className={styles.cardSubtitle}>Current distribution</p>
            </div>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.chartContainerSmall} style={{ position: 'relative' }}>
              <canvas ref={donutChartRef}></canvas>
              <div className={styles.donutCenter}>
                <div className={styles.donutValue}>{completedPercent}%</div>
                <div className={styles.donutLabel}>Completed</div>
              </div>
            </div>
            <div className={styles.chartLegend} style={{ marginTop: '24px' }}>
              <div className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: '#10b981' }}></span>
                Completed {completedPercent}%
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: '#1a237e' }}></span>
                In Progress {inProgressPercent}%
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: '#e8eaf6' }}></span>
                Pending {pendingPercent}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Grid */}
      <div className={`${styles.bottomGrid} ${(userRole === 'admin' || userRole === 'group_admin') ? styles.threeColumns : styles.twoColumns}`}>
        {/* Team Performance */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>Team Performance</h3>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.ratingContainer}>
              <div className={`${styles.ratingCircle} ${styles.large}`}>
                <span className={styles.ratingValue}>{completedPercent}%</span>
                <span className={styles.ratingLabel}>Overall</span>
              </div>
              <div className={`${styles.ratingCircle} ${styles.medium}`}>
                <span className={styles.ratingValue}>{inProgressPercent}%</span>
                <span className={styles.ratingLabel}>In Progress</span>
              </div>
              <div className={`${styles.ratingCircle} ${styles.small}`}>
                <span className={styles.ratingValue}>{pendingPercent}%</span>
                <span className={styles.ratingLabel}>Pending</span>
              </div>
            </div>
          </div>
        </div>

        {/* Top Groups - Only show for admin and group_admin, not for normal members */}
        {(userRole === 'admin' || userRole === 'group_admin') && (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>Top Groups</h3>
            </div>
            <div className={styles.cardBody}>
              <div className={styles.listItems}>
                {topPerformers.length > 0 ? (
                  topPerformers.map((performer, index) => (
                    <div key={performer.id} className={styles.listItem}>
                      <div className={styles.listItemIcon}>
                        {getGroupIcon(index)}
                      </div>
                      <div className={styles.listItemContent}>
                        <p className={styles.listItemTitle}>{performer.name}</p>
                        <div className={styles.progressBar}>
                          <div
                            className={`${styles.progressFill} ${styles[performer.color]}`}
                            style={{ width: `${Math.min(performer.progress, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className={styles.listItemValue}>{performer.progress}%</div>
                    </div>
                  ))
                ) : (
                  <div className={styles.emptyState}>No group data available</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Daily Tasks Chart */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>Daily Tasks</h3>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.chartContainerSmall}>
              <canvas ref={barChartRef}></canvas>
            </div>
            <div className={styles.chartLegend}>
              <div className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: '#1a237e' }}></span>
                Completed
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: '#e5e7eb' }}></span>
                Pending
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity - Full Width */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>Recent Activity</h3>
        </div>
        <div className={styles.cardBody}>
          <div className={styles.activityFeed}>
            {activities.length > 0 ? (
              activities.map(activity => (
                <div key={activity.id} className={styles.activityItem}>
                  <div className={`${styles.activityIcon} ${styles[activity.type]}`}>
                    {activity.type === 'success' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>}
                    {activity.type === 'warning' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>}
                    {activity.type === 'info' && <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>}
                  </div>
                  <div className={styles.activityContent}>
                    <p className={styles.activityTitle}>{activity.title}</p>
                    <p className={styles.activityMeta}>{activity.user} · {activity.group}</p>
                  </div>
                  <div className={styles.activityRight}>
                    {activity.points && (
                      <span className={styles.activityPoints}>+{activity.points} pts</span>
                    )}
                    <span className={styles.activityTime}>{activity.time}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className={styles.emptyState}>No recent activity</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
